import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import {
  borrowerName,
  getBorrowerLoans,
  getLoandiskBorrower,
  LoandiskBorrower,
  LoandiskLoan,
} from '../services/loandiskMirror';
import { supabase } from '../lib/supabase';

const formatMoney = (value: number | null) =>
  `₱${Number(value || 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value: string | null) => {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM dd, yyyy');
  } catch {
    return value;
  }
};

const Detail = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
  </Box>
);

// LoanDisk-only records are read-only here. Once a matching legacy collection
// profile exists, the collection list routes to that richer profile instead.
const LoandiskBorrowerProfile: React.FC = () => {
  const { uniqueNumber = '' } = useParams<{ uniqueNumber: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [borrower, setBorrower] = useState<LoandiskBorrower | null>(null);
  const [loans, setLoans] = useState<LoandiskLoan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!uniqueNumber) return;
    setLoading(true);
    try {
      const [borrowerData, loanData] = await Promise.all([
        getLoandiskBorrower(uniqueNumber),
        getBorrowerLoans(uniqueNumber),
      ]);
      if (!borrowerData) {
        throw new Error('Borrower was not found in the LoanDisk mirror.');
      }
      setBorrower(borrowerData);
      setLoans(loanData);
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load borrower details.');
    } finally {
      setLoading(false);
    }
  }, [uniqueNumber]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!uniqueNumber) return;
    const channel = supabase
      .channel(`agency-loandisk-borrower-${uniqueNumber}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'loandisk_loans',
          filter: `unique_number=eq.${uniqueNumber}`,
        },
        load
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'loandisk_borrowers',
          filter: `unique_number=eq.${uniqueNumber}`,
        },
        load
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [load, uniqueNumber]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !borrower) {
    return (
      <Container sx={{ py: 2, px: { xs: 1, sm: 3 } }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/collection-list')}>
          Back to collection list
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Borrower not found.'}
        </Alert>
      </Container>
    );
  }

  const name = borrowerName(borrower);
  const outstanding = loans.reduce((sum, loan) => sum + Number(loan.loan_balance || 0), 0);
  const activeLoans = loans.filter((loan) => Number(loan.loan_balance || 0) > 0);

  return (
    <Box sx={{ minHeight: '100%', py: { xs: 1, sm: 3 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg" disableGutters={isMobile} sx={{ px: { xs: 1, sm: 3 } }}>
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/collection-list')}
          sx={{ mb: 1 }}
        >
          Collection list
        </Button>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={borrower.photo_url || undefined}
              sx={{ width: { xs: 58, sm: 72 }, height: { xs: 58, sm: 72 }, bgcolor: 'primary.main' }}
            >
              {name.slice(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800 }}>
                {name}
              </Typography>
              <Typography color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {borrower.unique_number || 'No unique number'}
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Detail label="Outstanding" value={formatMoney(outstanding)} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Detail label="Active loans" value={activeLoans.length} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Detail label="Mobile" value={borrower.mobile} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Detail label="Last sync" value={formatDate(borrower.synced_at)} />
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Contact information
          </Typography>
          <Typography>
            {[borrower.address, borrower.city, borrower.province].filter(Boolean).join(', ')
              || 'No address available'}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {[borrower.email, borrower.mobile].filter(Boolean).join(' • ') || 'No contact details'}
          </Typography>
        </Paper>

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Loan ledger
        </Typography>
        <Stack spacing={1.25}>
          {loans.map((loan) => {
            const active = Number(loan.loan_balance || 0) > 0;
            return (
              <Card key={loan.loandisk_loan_id} variant="outlined">
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {loan.loan_product_name || 'Loan'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {loan.loan_number || 'No loan number'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={active ? 'warning' : 'success'}
                      label={loan.loan_status || (active ? 'Open' : 'Fully paid')}
                    />
                  </Stack>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={6} sm={3}>
                      <Detail label="Balance" value={formatMoney(loan.loan_balance)} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Detail label="Principal" value={formatMoney(loan.principal_amount)} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Detail label="Released" value={formatDate(loan.released_date)} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Detail label="Maturity" value={formatDate(loan.maturity_date)} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
          {loans.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No loans found for this borrower.</Typography>
            </Paper>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default LoandiskBorrowerProfile;
